import PowerPointSlideProcessor from '@app/services/export/ppt/slides/PowerPointSlideProcessor';
import IChapterOverviewSlideData from '@app/types/data/slides/IChapterOverviewSlideData';
import PptxGenJS from 'pptxgenjs';
import {
	createFullScreenCroppedImageForSlide,
	createImageForSlide,
	createPartnerLogoForSlide
} from '@app/services/export/ppt/utils/layoutUtils';
import { colors, presentationDimensions } from '@app/services/export/ppt/variables/constants';
import { gridCol } from '@app/services/export/ppt/utils/gridUtils';
import { copyBold, copyMini, h2 } from '@app/services/export/ppt/variables/typography';
import global from '@app/filters/global';
import { chunkArray } from '@app/services/export/ppt/utils/listUtils';

/**
 * Demonstration only - can be removed if chapter overview slides are not relevant to export
 */

export default class PowerPointChapterOverViewSlideProcessor extends PowerPointSlideProcessor<IChapterOverviewSlideData> {
	public async processSlideData(): Promise<void> {
		const slide: PptxGenJS.Slide = this.presentation.addSlide();
		const headlineY = 0.3;
		const textArray = [];
		const metaDataText = [];

		createFullScreenCroppedImageForSlide(slide, this.slideData.image);

		slide.addImage({
			path: 'assets/images/ppt-gradient-transparent-blue.png',
			x: 0,
			y: 0,
			w: '100%',
			h: '100%'
		});

		if (this.slideData.headline) {
			textArray.push({ text: this.slideData.headline, options: { ...h2, breakLine: true } });
		}

		if (this.slideData.elevation) {
			metaDataText.push(`${this.i18n.elevation}: ${global.formatElevation(this.slideData.elevation)}`);
		}

		let operatingTimes = this.mapOperatingTimes(this.slideData.operatingTimes);
		operatingTimes = operatingTimes || this.slideData.operatingTime;

		if (operatingTimes) {
			metaDataText.push(`${this.i18n.operatingTime}: ${operatingTimes}`);
		}

		if (metaDataText.length) {
			textArray.push({
				text: metaDataText.join('  ●  '),
				options: { ...copyBold }
			});
		}

		slide.addText(textArray, {
			y: headlineY,
			h: 1.4,
			...gridCol(1, 10),
			color: colors.white,
			align: 'center',
			valign: 'middle',
			glow: {
				color: '000000',
				size: 15,
				opacity: 0.35
			}
		});

		if (this.slideData.logo) {
			createPartnerLogoForSlide(slide, this.slideData.logo);
		}

		// list of chapters
		const validNavigationItems = this.slideData.navigationItems.filter(
			item => this.presentation.getSlideNumberById(item.id) > 0
		);

		const itemsPerRow = 9;
		const chunkedNavigationItems = chunkArray(validNavigationItems, itemsPerRow).reverse();
		const itemWidth = 0.9;
		const itemHeight = 0.51;
		const itemGap = 0.1;
		const labelHeight = 0.5;
		const bottomStartY = presentationDimensions.h - labelHeight - itemHeight - 0.3;

		chunkedNavigationItems.forEach((navigationItems, chunkIndex) => {
			const totalWidth = navigationItems.length * (itemWidth + itemGap) - itemGap;
			const rowStartX = (presentationDimensions.w - totalWidth) / 2;
			const rowStartY = bottomStartY - (itemHeight + labelHeight) * chunkIndex;

			navigationItems.forEach((navigationItem, itemIndex) => {
				const x = rowStartX + itemIndex * (itemWidth + itemGap);
				const y = rowStartY;

				// image
				if (navigationItem.image) {
					createImageForSlide(
						slide,
						{ ...navigationItem.image },
						{
							x,
							y,
							destinationWidth: itemWidth,
							destinationHeight: itemHeight,
							type: 'cover',
							hyperlink: { slide: this.presentation.getSlideNumberById(navigationItem.id) }
						}
					);
				}

				// label
				if (navigationItem.label) {
					slide.addText(navigationItem.label, {
						...copyMini,
						bold: true,
						x,
						y: y + itemHeight,
						w: itemWidth,
						h: labelHeight,
						valign: 'top',
						align: 'center',
						color: colors.white,
						margin: [0, 0, 0, 4]
					});
				}
			});
		});
	}
}
